import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import type { NavigateFunction } from 'react-router';
import { v4 as uuidv4 } from 'uuid';
import type { IDocumentState } from '@/store/reducers/DocumentReducer';
import { IDocumentData } from '@models/document/IDocumentData';
import { IDocumentDraft } from '@models/documentDraft/IDocumentDraft';
import { IDocumentDraftData } from '@models/documentDraft/IDocumentDraftData';
import { DocumentDraftService } from '@services/DocumentDraftService';
import type { DocumentDraftStripState } from '@organisms/document/DocumentDraftStrip/DocumentDraftStrip.types';
import { bypassNextNavigation } from '@srm/ui/lib/navigationGuard';
import { useLatest } from '@srm/ui/hooks/useLatest';
import { createValueStore, type IValueStore } from '@srm/ui/lib/valueStore';
import { RouteNames } from '@/router/router.types';
import { stripDraftFiles } from './draftPayload';

/** Хвост маршрута создания у черновиков из шаблона до перехода на uuid-адреса. */
const LEGACY_TEMPLATE_TAIL = /\/-1$/;

/**
 * Канонический адрес черновика — путь создания без идентификатора черновика и без легаси-хвоста
 * `/-1`. Под ним черновик хранится на сервере (`url`) и по нему виджет строит ссылку `${url}/${id}`.
 */
export const draftBasePath = (pathname: string, draftId?: string): string => {
    let path = pathname.replace(LEGACY_TEMPLATE_TAIL, '');
    if (draftId && draftId !== '-1' && path.endsWith(`/${draftId}`)) {
        path = path.slice(0, -(draftId.length + 1));
    }
    return path;
};

/** Ссылка на черновик для навигации: легаси-строки с хвостом `/-1` в `url` нормализуются. */
export const draftHref = (draft: Pick<IDocumentDraft, 'id' | 'url'>): string =>
    `${draft.url.replace(LEGACY_TEMPLATE_TAIL, '')}/${draft.id}`;

const ON_BASE_PREFIX = /^\/document\/createOnBase\/([^/]+)(?:\/|$)/;

/**
 * Происхождение черновика: «с нуля или из шаблона» против «на базе документа N».
 *
 * Списки карточки (полоса и меню) показывают черновики одного происхождения с открытой формой.
 * Шаблон лишь предзаполняет ту же форму — такие черновики смешивать с обычными можно. А вот
 * черновик, начатый от другого документа, — это другая работа с другим родителем, и в строке
 * списка этого не видно: там только имя и время. Ошибиться и дослать черновик к чужому
 * родителю слишком легко, поэтому базы разделены.
 */
export const draftOrigin = (url: string): string => {
    const onBase = ON_BASE_PREFIX.exec(url);
    return onBase ? `base:${onBase[1]}` : 'action';
};

const STRIP_DISMISSED_PREFIX = 'documentDraftStrip:';

const isStripDismissed = (basePath: string): boolean => {
    try {
        return sessionStorage.getItem(STRIP_DISMISSED_PREFIX + basePath) === '1';
    } catch {
        return false;
    }
};

const rememberStripDismissed = (basePath: string) => {
    try {
        sessionStorage.setItem(STRIP_DISMISSED_PREFIX + basePath, '1');
    } catch {
        // sessionStorage недоступен (приватный режим) — полоса просто покажется снова.
    }
};

/** Роутерные параметры, которые checkDraft обязан читать актуальными в момент вызова. */
export interface DocumentDraftRouteParams {
    id?: string;
    draftId?: string;
    actId?: string;
    templateId?: string;
    baseDocId?: string;
    pathname: string;
    search?: string;
    hash?: string;
}

export interface UseDocumentDraftParams {
    doc: IDocumentState;
    routeParams: DocumentDraftRouteParams;
    autoSaveDocument: boolean;
    navigate: NavigateFunction;
    setDocumentData: (data: IDocumentData) => void;
    setTurnOffInvokeChangesOnNew: (value: boolean | undefined) => void;
}

export interface UseDocumentDraft {
    /** Текущий черновик (render-state — для индикаторов/дебага). */
    draft: IDocumentDraftData | undefined;
    /** Полный payload черновика без ререндера карточки (нужен autosave). */
    draftRef: RefObject<IDocumentDraftData | undefined>;
    /** External-store индикатора «черновик»; set — только из обработчиков и эффектов, не из рендера. */
    draftStatusStore: IValueStore<boolean>;
    /** baseline последнего сохранения (draft-id-keyed) — ребро к useDocumentAutosave. */
    lastSavedDataStrRef: RefObject<string | null>;
    lastSavedDraftIdRef: RefObject<string | null>;
    /** Полоса под тулбаром: другие черновики этого типа / не найден / уже стал документом. */
    strip: DocumentDraftStripState | null;
    dismissStrip: () => void;
    /** Открыть другой черновик того же действия, не покидая карточку (адрес заменяется). */
    openDraft: (draft: Pick<IDocumentDraft, 'id' | 'url'>) => Promise<void>;
    /** Открыть документ, в который превратился черновик (надгробие). */
    openConsumedDocument: (docId: string) => void;
    /**
     * Перечитать активный черновик с сервера (после конфликта версий). Локальные правки теряются.
     * Черновик удалили в другой вкладке — правки этой остаются и сохранятся заново под тем же id.
     */
    reloadActiveDraft: () => Promise<void>;
    /** Дать имя активному черновику (пустое — снять). Обновляет render-state. */
    renameActiveDraft: (name: string | null) => Promise<void>;
    /**
     * Удалить активный черновик на сервере и начать новую сессию под новым адресом. Данные формы
     * перечитывает вызывающий (retryDocumentLoad) — хук ими не владеет. Reject — не удалён, форма та же.
     */
    deleteActiveDraft: () => Promise<void>;
    /** Удалить другой черновик этого действия (из списка в полосе или меню капсулы). Reject — не удалён. */
    removeListedDraft: (draft: Pick<IDocumentDraft, 'id'>) => Promise<void>;
    /**
     * Номер сессии редактирования: растёт при каждой новой форме и каждом открытом черновике,
     * но НЕ при синхронизации id с сервером. Автосейв пишет на сервер только после первой правки
     * пользователя в текущей сессии.
     */
    draftSessionRef: RefObject<number>;
    /**
     * Тот же номер сессии, но как render-значение: ref не будит эффекты, а смену сессии
     * нужно видеть снаружи (индикатор автосейва гасит на ней время прошлого черновика).
     */
    draftSession: number;
    /** Установить активный черновик (ref + store + render-state). */
    setActiveDraft: (nextDraft: IDocumentDraftData | undefined) => void;
    /**
     * Перевести форму на новый id черновика в той же сессии (адрес заменяется). Нужен, когда id из
     * ссылки занят чужим черновиком: правки пользователя сохраняются заново, уже под своим id.
     */
    rekeyActiveDraft: () => IDocumentDraftData | undefined;
    /** Резолвинг черновика вокруг загрузки данных (по draftId из адреса либо новый с fresh-id). */
    checkDraft: (loadData: () => void | Promise<void>) => Promise<void>;
}

/**
 * Зона ответственности «черновик»: идентичность (uuid в адресе), загрузка по id, полоса
 * возобновления, имя/удаление и baseline автосейва. Хук ВЛАДЕЕТ своим состоянием/refs; cross-hook
 * связи (draftRef, setActiveDraft, lastSaved*) отдаются наружу явными полями возврата.
 */
export const useDocumentDraft = ({
    doc,
    routeParams,
    autoSaveDocument,
    navigate,
    setDocumentData,
    setTurnOffInvokeChangesOnNew,
}: UseDocumentDraftParams): UseDocumentDraft => {
    const [draft, setDraft] = useState<IDocumentDraftData>();
    const [strip, setStrip] = useState<DocumentDraftStripState | null>(null);
    const draftRef = useRef<IDocumentDraftData | undefined>(undefined);
    const draftSessionRef = useRef(0);
    const [draftSession, setDraftSession] = useState(0);
    // baseline сравнения «менялось ли с прошлого сохранения» — строка payload и id черновика,
    // к которому она относится (id меняется при смене черновика). Читается autosave.
    const lastSavedDataStrRef = useRef<string | null>(null);
    const lastSavedDraftIdRef = useRef<string | null>(null);
    // Открытие другого черновика во время загрузки данных нового документа: ответ сервера
    // перезаписал бы данные черновика. Ждём окончания загрузки и применяем черновик — только
    // если путь карточки тот же (Document не перемонтируется при смене :id).
    const pendingOpenRef = useRef<{ draft: IDocumentDraftData; pathname: string } | null>(null);
    // Номер последнего openDraft: ответ на более раннее открытие не перетирает выбранный позже черновик.
    const openDraftRequestRef = useRef(0);
    const [draftStatusStore] = useState(() => createValueStore(false));

    // Зеркала doc/params: checkDraft/openDraft читают АКТУАЛЬНЫЕ значения в момент вызова, а не
    // из замыкания (иначе при смене только draftId внутри одного route-pattern автосейв уйдёт под
    // старый URL, а гард загрузки не увидит свежий isLoading).
    const docIsLoadingRef = useLatest(doc.isLoading);
    const routeParamsRef = useLatest(routeParams);
    // navigate из useNavigate меняет идентичность при каждой смене pathname (BrowserRouter): в deps
    // он заставил бы checkDraft → loadNewDocumentByAction → эффект загрузки перезапуститься сразу
    // после нашего же replace с uuid — двойная загрузка документа и сброс формы.
    const navigateRef = useLatest(navigate);

    const makeFreshDraft = useCallback(
        (url: string, id: string): IDocumentDraftData => {
            const { actId, templateId, baseDocId } = routeParamsRef.current;
            return {
                url,
                id,
                name: null,
                autoTitle: '',
                actionId: actId ?? null,
                templateId: templateId ?? null,
                baseDocId: baseDocId ?? null,
                version: 0,
            };
        },
        [routeParamsRef],
    );

    const setActiveDraft = useCallback(
        (nextDraft: IDocumentDraftData | undefined) => {
            draftRef.current = nextDraft;
            draftStatusStore.set(nextDraft !== undefined);
            setDraft(nextDraft);
            // Адрес обязан следовать за реальным id черновика, иначе F5 откроет пустую форму.
            const { id: docId, draftId, search = '', hash = '' } = routeParamsRef.current;
            if (nextDraft && !docId && draftId && nextDraft.id !== draftId) {
                bypassNextNavigation();
                void navigateRef.current(`${draftHref(nextDraft)}${search}${hash}`, { replace: true });
                setStrip((prev) => {
                    if (prev?.kind !== 'existing') return prev;
                    const drafts = prev.drafts.filter((item) => item.id !== nextDraft.id);
                    return drafts.length > 0 ? { kind: 'existing', drafts } : null;
                });
            }
        },
        [draftStatusStore, navigateRef, routeParamsRef],
    );

    const rekeyActiveDraft = useCallback((): IDocumentDraftData | undefined => {
        const current = draftRef.current;
        if (!current) return undefined;
        // data — baseline прошлого сохранения: у нового id его нет, иначе первый PUT отсеялся бы как «без изменений».
        const next: IDocumentDraftData = {
            ...current,
            id: uuidv4(),
            name: null,
            version: 0,
            createdAt: null,
            updatedAt: null,
            data: undefined,
        };
        setActiveDraft(next);
        return next;
    }, [setActiveDraft]);

    const beginSession = useCallback(
        (nextDraft: IDocumentDraftData) => {
            draftSessionRef.current += 1;
            setDraftSession(draftSessionRef.current);
            setActiveDraft(nextDraft);
        },
        [setActiveDraft],
    );

    const applyDraft = useCallback(
        (loaded: IDocumentDraftData) => {
            // Черновики, сохранённые до исключения files из payload, несут строки вложений
            // без бинарников — восстанавливать их нельзя (см. draftPayload).
            setDocumentData(stripDraftFiles(JSON.parse(loaded.data!) as IDocumentData));
            beginSession(loaded);
            setTurnOffInvokeChangesOnNew(true);
            setStrip(null);
        },
        [beginSession, setDocumentData, setTurnOffInvokeChangesOnNew],
    );

    useEffect(() => {
        if (doc.isLoading) return;
        const pending = pendingOpenRef.current;
        if (!pending) return;
        pendingOpenRef.current = null;
        if (pending.pathname !== routeParamsRef.current.pathname) return;
        applyDraft(pending.draft);
    }, [applyDraft, doc.isLoading, routeParamsRef]);

    const dismissStrip = useCallback(() => {
        const { pathname, draftId } = routeParamsRef.current;
        if (strip?.kind === 'existing') {
            rememberStripDismissed(draftBasePath(pathname, draftId));
        }
        setStrip(null);
    }, [routeParamsRef, strip]);

    // Другие черновики этого действия (кроме текущего) — для полосы «продолжить последний».
    const loadExistingDrafts = useCallback(
        async (basePath: string, currentId: string) => {
            if (isStripDismissed(basePath)) return;
            try {
                const items = await DocumentDraftService.list(routeParamsRef.current.actId);
                // За время запроса могли уйти на другое действие или сменить черновик (replace с uuid
                // применяется роутером асинхронно, поэтому сравниваем канонический путь, а не pathname).
                const current = routeParamsRef.current;
                if (draftBasePath(current.pathname, current.draftId) !== basePath) return;
                if (draftRef.current?.id !== currentId) return;
                // Сервер отдаёт список новейшими первыми — порядок сохраняется. Берём черновики того
                // же происхождения (см. draftOrigin): фильтр по базовому пути прятал ещё и шаблонные,
                // и полоса считала не то же, что лаунчер «Создать документ».
                const origin = draftOrigin(current.pathname);
                const drafts = items.filter((item) => item.id !== currentId && draftOrigin(item.url) === origin);
                setStrip(drafts.length > 0 ? { kind: 'existing', drafts } : null);
            } catch (error) {
                console.error('Не удалось получить список черновиков', error);
            }
        },
        [routeParamsRef],
    );

    // Новая сессия под свежим uuid: адрес заменяется, данные (если передан loadData) грузятся как
    // для нового документа.
    const startFresh = useCallback(
        (loadData: (() => void | Promise<void>) | undefined, nextStrip: DocumentDraftStripState | null) => {
            const { draftId, pathname, search = '', hash = '' } = routeParamsRef.current;
            const basePath = draftBasePath(pathname, draftId);
            const freshId = uuidv4();
            bypassNextNavigation();
            void navigateRef.current(`${basePath}/${freshId}${search}${hash}`, { replace: true });
            setStrip(nextStrip);
            setTurnOffInvokeChangesOnNew(false);
            void loadData?.();
            beginSession(makeFreshDraft(basePath, freshId));
            if (!nextStrip) {
                void loadExistingDrafts(basePath, freshId);
            }
        },
        [beginSession, loadExistingDrafts, makeFreshDraft, navigateRef, routeParamsRef, setTurnOffInvokeChangesOnNew],
    );

    const checkDraft = useCallback(
        async (loadData: () => void | Promise<void>) => {
            const { id: currentId, draftId, pathname } = routeParamsRef.current;
            if (!autoSaveDocument || currentId) {
                setTurnOffInvokeChangesOnNew(false);
                setStrip(null);
                setActiveDraft(undefined);
                await loadData?.();
                return;
            }
            const basePath = draftBasePath(pathname, draftId);

            if (draftId && draftId !== '-1') {
                try {
                    const lookup = await DocumentDraftService.get(draftId);
                    // За время запроса могли уйти на другой документ (Document не перемонтируется
                    // при смене :id): не вписываем черновик дока A в текущий док B.
                    if (pathname !== routeParamsRef.current.pathname) return;
                    if (lookup.kind === 'draft' && lookup.draft.data) {
                        applyDraft({ ...lookup.draft, url: basePath });
                        return;
                    }
                    if (lookup.kind === 'consumed') {
                        // Черновик уже стал документом: новая форма под новым адресом и ссылка на документ.
                        startFresh(loadData, { kind: 'consumed', docId: lookup.docId });
                        return;
                    }
                } catch (error) {
                    // Сеть или 5xx: на сервере черновик, скорее всего, есть. Открыть форму под тем же
                    // id нельзя — первая правка ушла бы без If-Match и перетёрла бы его содержимое.
                    console.error('Не удалось загрузить черновик', error);
                    startFresh(loadData, { kind: 'unavailable', draft: { id: draftId, url: basePath } });
                    return;
                }
                // По ссылке черновика нет: либо его ещё не сохраняли (форму открыли и обновили
                // страницу — запись появляется только с первой правки), либо он удалён или чужой.
                // Различить нельзя, поэтому молча открываем новую форму под тем же адресом; полоса
                // «не найден» показывается только при явном открытии черновика (openDraft).
                setStrip(null);
                setTurnOffInvokeChangesOnNew(false);
                void loadData?.();
                beginSession(makeFreshDraft(basePath, draftId));
                return;
            }

            // Голый адрес создания: черновик получает идентификатор сразу и кладёт его в адрес.
            // На сервер ничего не пишется, пока пользователь не начнёт заполнять форму.
            startFresh(loadData, null);
        },
        [
            autoSaveDocument,
            applyDraft,
            beginSession,
            makeFreshDraft,
            routeParamsRef,
            setActiveDraft,
            setTurnOffInvokeChangesOnNew,
            startFresh,
        ],
    );

    const openDraft = useCallback(
        async (target: Pick<IDocumentDraft, 'id' | 'url'>) => {
            const { search = '', hash = '', pathname: startPathname } = routeParamsRef.current;
            const nextPathname = draftHref(target);
            const request = ++openDraftRequestRef.current;
            bypassNextNavigation();
            void navigateRef.current(`${nextPathname}${search}${hash}`, { replace: true });
            setStrip(null);
            // routeParamsRef догоняет адрес только рендером карточки, а ответ черновика бывает
            // быстрее: прежний адрес значит «переход ещё не применён», а не «ушли с карточки».
            const isCurrentRequest = () => {
                if (openDraftRequestRef.current !== request) return false;
                const { pathname } = routeParamsRef.current;
                return pathname === nextPathname || pathname === startPathname;
            };
            // Черновик не применён — форма осталась прежней, адрес возвращаем к её черновику, иначе
            // F5 открыл бы не то, что на экране.
            const restoreAddress = () => {
                const current = draftRef.current;
                if (!current) return;
                bypassNextNavigation();
                void navigateRef.current(`${draftHref(current)}${search}${hash}`, { replace: true });
            };
            try {
                const lookup = await DocumentDraftService.get(target.id);
                if (!isCurrentRequest()) return;
                if (lookup.kind === 'consumed') {
                    setStrip({ kind: 'consumed', docId: lookup.docId });
                    return;
                }
                if (lookup.kind !== 'draft' || !lookup.draft.data) {
                    restoreAddress();
                    setStrip({ kind: 'notFound' });
                    return;
                }
                const next = { ...lookup.draft, url: draftBasePath(target.url) };
                if (docIsLoadingRef.current) {
                    pendingOpenRef.current = { draft: next, pathname: nextPathname };
                    return;
                }
                applyDraft(next);
            } catch (error) {
                console.error('Не удалось открыть черновик', error);
                if (!isCurrentRequest()) return;
                restoreAddress();
                setStrip({ kind: 'unavailable', draft: { id: target.id, url: draftBasePath(target.url) } });
            }
        },
        [applyDraft, docIsLoadingRef, navigateRef, routeParamsRef],
    );

    const openConsumedDocument = useCallback(
        (docId: string) => {
            bypassNextNavigation();
            void navigateRef.current(RouteNames.DOCUMENT.replace(':id', docId));
        },
        [navigateRef],
    );

    const reloadActiveDraft = useCallback(async () => {
        const current = draftRef.current;
        if (!current) return;
        const lookup = await DocumentDraftService.get(current.id);
        if (draftRef.current?.id !== current.id) return;
        if (lookup.kind === 'draft' && lookup.draft.data) {
            applyDraft({ ...lookup.draft, url: current.url });
        } else if (lookup.kind === 'consumed') {
            setStrip({ kind: 'consumed', docId: lookup.docId });
        } else {
            // Удалён в другой вкладке: версии больше нет — новая сессия снимает блокировку конфликта,
            // следующая правка создаст запись заново.
            beginSession({ ...current, version: 0 });
        }
    }, [applyDraft, beginSession]);

    const renameActiveDraft = useCallback(async (name: string | null) => {
        const current = draftRef.current;
        if (!current) return;
        await DocumentDraftService.rename(current.id, name);
        if (draftRef.current?.id !== current.id) return;
        const next = { ...draftRef.current, name: name?.trim() || null };
        draftRef.current = next;
        setDraft(next);
    }, []);

    const deleteActiveDraft = useCallback(async () => {
        const current = draftRef.current;
        if (!current) return;
        await DocumentDraftService.remove(current.id);
        if (draftRef.current?.id !== current.id) return;
        startFresh(undefined, null);
    }, [startFresh]);

    const removeListedDraft = useCallback(async (target: Pick<IDocumentDraft, 'id'>) => {
        await DocumentDraftService.remove(target.id);
        setStrip((prev) => {
            if (prev?.kind !== 'existing') return prev;
            const drafts = prev.drafts.filter((item) => item.id !== target.id);
            return drafts.length > 0 ? { kind: 'existing', drafts } : null;
        });
    }, []);

    return {
        draft,
        draftRef,
        draftStatusStore,
        lastSavedDataStrRef,
        lastSavedDraftIdRef,
        strip,
        dismissStrip,
        openDraft,
        openConsumedDocument,
        reloadActiveDraft,
        renameActiveDraft,
        deleteActiveDraft,
        removeListedDraft,
        draftSessionRef,
        draftSession,
        setActiveDraft,
        rekeyActiveDraft,
        checkDraft,
    };
};
